/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useTranslation } from "react-i18next";

import { type FC, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useClient } from "../ClientContext";
import { useRoomIdentifier } from "../UrlParams";
import { LoadingPage, ErrorPage } from "../FullScreenView";
import { BackendError } from "../utils/backend";
import { widget } from "../widget";
import { staffRoomTarget } from "./staffRoomTarget";

export const StaffCallRedirect: FC = () => {
  const { t } = useTranslation();
  const { client } = useClient(),
    location = useLocation(),
    navigate = useNavigate();
  const { roomId, roomAlias } = useRoomIdentifier();
  const [error, setError] = useState<Error>();
  useEffect(() => {
    if (!client) return;
    const controller = new AbortController();
    const relative = location.pathname + location.search + location.hash;
    const run = async (): Promise<void> => {
      const id =
        roomId ??
        (roomAlias
          ? (await client.getRoomIdForAlias(roomAlias)).room_id
          : undefined);
      if (!id) throw new Error(t("instance.ui.room_identifier_required"));
      const target=staffRoomTarget(relative,id);
      if(!controller.signal.aborted)window.location.assign(target);
    };
    void run().catch((cause) => {
      if (controller.signal.aborted) return;
      if (cause instanceof BackendError && cause.status === 428) {
        // An exceptionally missing/revoked pair is restored by the ordinary
        // cabinet login, never by an intermediate password form on join.
        void navigate("/login", { state: { from: location }, replace: true });
        return;
      }
      setError(
        cause instanceof Error
          ? cause
          : new Error(t("instance.ui.join_failed")),
      );
    });
    return () => controller.abort();
  }, [client, location, navigate, roomId, roomAlias, t]);
  return error ? <ErrorPage widget={widget} error={error} /> : <LoadingPage />;
};
